import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  supabase,
  SUPABASE_FUNCTIONS_URL,
  SUPABASE_ANON_KEY,
} from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";

export interface MondayGroup {
  id: string;
  title: string;
}

export type MatchLevel = "exact" | "phase" | "project_only" | "skipped";

export interface PreviewItem {
  monday_item_id: number;
  name: string;
  group_title: string | null;
  project_code: string | null;
  project_id: string | null;
  parsed_phase: string | null;
  parsed_task: string;
  budget_line_id: string | null;
  match_level: MatchLevel;
  skip_reason?: string;
  /** Antall time_tracking-sessions Monday rapporterer på itemet. */
  sessions_count: number;
  /** Totalt antall sekunder logget på itemet (sum av sessions). */
  total_seconds: number;
  /** VIB-939: sessions på itemet som ennå ikke finnes i Sølvrev. */
  new_sessions_count?: number;
  /** VIB-939: sessions allerede importert (finnes i time_sessions). */
  existing_sessions_count?: number;
}

export interface ImportStats {
  totalInGroups: number;
  imported: number;
  skipped: number;
  /** Items som admin har slått av i preview (override.skip=true). */
  skippedByAdmin?: number;
  matchDistribution: Record<string, number>;
  /** VIB-939: totalt antall sessions som vil bli nyimportert. */
  newSessions?: number;
  /** VIB-939: totalt antall sessions som allerede finnes (dedupliseres bort). */
  existingSessions?: number;
  /** Antall (budget_line, user)-par som fikk oppdatert tildelte timer. */
  assignmentsUpserted?: number;
  /** Foreldreløse assignments (uten time_entry-grunnlag) som ble nullstilt. */
  assignmentsZeroed?: number;
  /** Per-item insert-feil som edge function fanget men ikke kunne fullføre. */
  insertErrors?: Array<{ monday_item_id: number; stage: string; message: string; code: string | null }>;
}

export interface ImportOverride {
  phase?: string | null;
  budget_line_id?: string | null;
  /** Hvis true: hopp over itemet ved bekreft. */
  skip?: boolean;
  /**
   * VIB-724a: Når admin oppretter et internprosjekt inline fra preview-raden,
   * lagrer vi det nye prosjekt-ID-et her slik at UI markerer raden som matchet
   * mot prosjektet. Edge function plukker dette opp på neste dry-run via
   * project_code (interne prosjekter får INT##-kode), så dette feltet er
   * primært visuelt frem til admin re-kjører dry-run.
   *
   * VIB-779: Nå brukt også av Import-gap-dialog for å overstyre project-matchen
   * direkte i edge function (uten å gå via project_code).
   */
  project_id?: string | null;
  /** VIB-794: Admin-gitt kommentar — brukes i stedet for Monday text-kolonne. */
  comment?: string | null;
}

export interface ImportResponse {
  success: boolean;
  stats?: ImportStats;
  preview?: PreviewItem[];
  error?: string;
}

export interface ImportLogRow {
  id: string;
  source: string;
  user_id: string | null;
  board_id: number | null;
  group_ids: string[] | null;
  items_imported: number;
  items_skipped: number;
  match_distribution: Record<string, number> | null;
  dry_run: boolean;
  created_at: string;
  created_by: string | null;
}

/** Auto-filter for grupper vi normalt vil ekskludere fra import. */
const EXCLUDE_KEYWORDS = ["intern", "tilbud", "pitch", "treatment", "avspasering", "ferie", "timer til gode", "fri"];

export function shouldExcludeGroup(title: string): boolean {
  const lower = title.toLowerCase();
  return EXCLUDE_KEYWORDS.some((kw) => lower.includes(kw));
}

async function callFunction<T>(
  name: string,
  init: { method?: string; body?: unknown; query?: Record<string, string> } = {},
): Promise<T> {
  const url = new URL(`${SUPABASE_FUNCTIONS_URL}/${name}`);
  if (init.query) {
    for (const [k, v] of Object.entries(init.query)) url.searchParams.set(k, v);
  }
  // MEDIUM-11: import-funksjonene krever nå admin (requireAdmin via getUser).
  // Send ekte session-token (ikke anon-nøkkel) så innlogget admin slipper gjennom.
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(url.toString(), {
    method: init.method ?? "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session?.access_token ?? ""}`,
      apikey: SUPABASE_ANON_KEY,
    },
    body: init.body ? JSON.stringify(init.body) : undefined,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${name} (${res.status}): ${text.slice(0, 300)}`);
  }
  return (await res.json()) as T;
}

/** Hent grupper fra et Monday-board (for admin-velger). */
export function useMondayBoardGroups(boardId: number | null) {
  return useQuery<MondayGroup[]>({
    queryKey: ["monday-board-groups", boardId],
    enabled: !!boardId,
    queryFn: async () => {
      const res = await callFunction<{ success: boolean; groups?: MondayGroup[]; error?: string }>(
        "monday-board-groups",
        { method: "GET", query: { board_id: String(boardId) } },
      );
      if (!res.success) throw new Error(res.error ?? "Ukjent feil");
      return res.groups ?? [];
    },
    staleTime: 60 * 1000,
  });
}

/** Trigger import (eller dry-run) for én ansatt. */
export function useMondayImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      userId: string;
      boardId: number;
      groupIds: string[];
      dryRun: boolean;
      triggeredBy?: string | null;
      overrides?: Record<string, ImportOverride>;
      /** Filtrér til disse project_codes. Tom/undefined = alle. */
      projectCodes?: string[];
    }): Promise<ImportResponse> => {
      const res = await callFunction<ImportResponse>("import-monday-time-data", {
        method: "POST",
        body: input,
      });
      if (!res.success) throw new Error(res.error ?? "Import feilet");
      return res;
    },
    onSuccess: async (data, vars) => {
      await invalidateFor(qc, "import_log");
      if (!vars.dryRun) {
        // Bred invalidering: én Monday-import oppdaterer assignments og
        // time_entries på tvers av mange prosjekter (typisk én ansatts
        // board → alle vedkommendes prosjekter), så vi kan ikke målrette
        // mot en spesifikk projectId her (VIB-649). Vi sender userId fra
        // input slik at user-spesifikke keys treffes presist.
        await Promise.all([
          invalidateFor(qc, "time_entries", { userId: vars.userId }),
          invalidateFor(qc, "budget_line_assignments", { userId: vars.userId }),
        ]);
        const s = data.stats;
        const errs = s?.insertErrors ?? [];
        if (errs.length > 0) {
          const first = errs[0];
          toast.error(
            `Import: ${s?.imported ?? 0} ok, ${errs.length} feilet. ` +
              `Første feil (${first.code ?? "?"} på ${first.stage}): ${first.message.slice(0, 200)}`,
            { duration: 15000 },
          );
        } else {
          toast.success(
            s
              ? `Importert ${s.imported}, hoppet over ${s.skipped} (av ${s.totalInGroups})` +
                (s.newSessions != null
                  ? ` — ${s.newSessions} nye sessions, ${s.existingSessions ?? 0} fra før`
                  : "") +
                (s.assignmentsUpserted ? ` — ${s.assignmentsUpserted} tildelinger oppdatert` : "")
              : "Import fullført",
          );
        }
      }
    },
    onError: (err) => {
      toast.error(`Import feilet: ${(err as Error).message}`);
    },
  });
}

/** Ansatte som har monday_board_id satt (kandidater for import). */
export interface MondayUserCandidate {
  id: string;
  full_name: string;
  monday_board_id: number;
}

export function useMondayUsers() {
  return useQuery<MondayUserCandidate[]>({
    queryKey: ["monday-users"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("users")
        .select("id, full_name, monday_board_id")
        .eq("is_active", true)
        .not("monday_board_id", "is", null)
        .order("full_name");
      if (error) throw error;
      return (data ?? []) as MondayUserCandidate[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Kandidat-prosjekter for per-prosjekt-import (project_code + navn). */
export interface ImportProjectCandidate {
  id: string;
  project_code: string;
  name: string | null;
}

export function useImportProjects() {
  return useQuery<ImportProjectCandidate[]>({
    queryKey: ["import-projects"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("id, project_code, name")
        .not("project_code", "is", null)
        .is("deleted_at", null)
        .order("project_code", { ascending: false });
      if (error) throw error;
      return (data ?? []) as ImportProjectCandidate[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Budget_lines for et sett av prosjekter — brukes i preview-redigeringen. */
export interface BudgetLineLite {
  id: string;
  project_id: string;
  phase: string | null;
  description: string | null;
}

export function useBudgetLinesForProjects(projectIds: string[]) {
  return useQuery<BudgetLineLite[]>({
    queryKey: ["budget-lines-for-projects", [...projectIds].sort().join(",")],
    enabled: projectIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("budget_lines")
        .select("id, project_id, phase, description")
        .in("project_id", projectIds);
      if (error) throw error;
      return (data ?? []) as BudgetLineLite[];
    },
    staleTime: 60 * 1000,
  });
}

export type ImportEntityType = "time_entries" | "comments";

/** Tidligere import-kjøringer fra import_log. */
export function useImportLog(
  userId?: string | null,
  entityType?: ImportEntityType,
) {
  return useQuery<ImportLogRow[]>({
    queryKey: ["import-log", userId ?? "all", entityType ?? "all"],
    queryFn: async () => {
      let q = supabase
        .from("import_log")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(50);
      if (userId) q = q.eq("user_id", userId);
      if (entityType) q = q.eq("entity_type", entityType);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as ImportLogRow[];
    },
    staleTime: 30 * 1000,
  });
}
